// P1-010: Mock PrepareResult for Phase 2/3 independent development
// All stats computed via AgentStatCalc formulas from agent-configs.ts base stats.
import type { PrepareResult } from '@/shared/types/phase-contracts';
import { AgentType, ElementType, SkillEffectType } from '@/shared/types/common';

/**
 * Mid-game team of 5 starter agents (all 2-star) at reasonable levels.
 *
 * Hardware setup corresponds to:
 *   2x CPU-basic (compute=4, power=2, heat=2)
 *   1x GPU-entry  (compute=5, power=3, heat=4)
 *   1x GPU-high   (compute=12, power=7, heat=10)
 *   1x TPU        (compute=8, power=2, heat=3)  -> specialEffect REDUCE_CD 1
 *   1x cooler-water (cooling=12, power=1)        -> specialEffect REDUCE_OVERLOAD_THRESHOLD 20
 *   1x PSU-high   (powerLimit=15)
 * Totals: compute=29, powerLimit=15, powerUsage=15, heatGen=19, cooling=12
 * Heat > cooling => overloadLevel='light', overloadValue=25
 *
 * But we set a slightly better scenario where cooling is adequate:
 *   Add 1x cooler-air (cooling=5)
 *   -> totalCooling=17, heatGen=19 still exceeds, OR
 *   Replace with a less-hot setup. See values below.
 */
export const MOCK_PREPARE_RESULT: PrepareResult = {
  team: [
    {
      // backend-athena: 2-star, level 10
      // ATK = round(80 * (1 + 0.08 * 9)) = round(80 * 1.72) = round(137.6) = 138
      // DEF = round(60 * (1 + 0.08 * 9)) = round(60 * 1.72) = round(103.2) = 103
      // HP  = round(400 * (1 + 0.05 * 9)) = round(400 * 1.45) = 580
      // CritRate = 5 + 2 * 2 = 9
      // ComputeCost = ceil(3 + (2-1)*0.5 + 10/10) = ceil(4.5) = 5
      agentId: 'backend-athena',
      name: 'Athena-Backend-v2.1',
      type: AgentType.BACKEND,
      rarity: 2,
      level: 10,
      attack: 138,
      defense: 103,
      hp: 580,
      critRate: 9,
      computeCost: 5,
      elementAffinity: ElementType.RED,
      activeSkill: {
        skillId: 'code-refactor',
        name: '代码重构',
        description: '输出高质量代码，造成额外伤害',
        cooldown: 4,
        effects: [
          { type: SkillEffectType.DAMAGE, value: 150, duration: 0, target: 'enemy' },
          { type: SkillEffectType.BUFF_ATK, value: 0.2, duration: 2, target: 'self' },
        ],
      },
    },
    {
      // frontend-aria: 2-star, level 8
      // ATK = round(60 * (1 + 0.08 * 7)) = round(60 * 1.56) = round(93.6) = 94
      // DEF = round(80 * (1 + 0.08 * 7)) = round(80 * 1.56) = round(124.8) = 125
      // HP  = round(400 * (1 + 0.05 * 7)) = round(400 * 1.35) = 540
      // CritRate = 5 + 2 * 2 = 9
      // ComputeCost = ceil(2 + (2-1)*0.5 + 8/10) = ceil(3.3) = 4
      agentId: 'frontend-aria',
      name: 'Aria-Frontend-v1.8',
      type: AgentType.FRONTEND,
      rarity: 2,
      level: 8,
      attack: 94,
      defense: 125,
      hp: 540,
      critRate: 9,
      computeCost: 4,
      elementAffinity: ElementType.BLUE,
      activeSkill: {
        skillId: 'ui-shield',
        name: 'UI防御壁',
        description: '构建前端防御层，减少过载惩罚',
        cooldown: 5,
        effects: [
          { type: SkillEffectType.BUFF_DEF, value: 5, duration: 2, target: 'team' },
        ],
      },
    },
    {
      // testing-scout: 2-star, level 10
      // ATK = round(70 * (1 + 0.08 * 9)) = round(70 * 1.72) = round(120.4) = 120
      // DEF = round(70 * (1 + 0.08 * 9)) = round(70 * 1.72) = round(120.4) = 120
      // HP  = round(400 * (1 + 0.05 * 9)) = round(400 * 1.45) = 580
      // CritRate = 5 + 2 * 2 = 9
      // ComputeCost = ceil(2 + (2-1)*0.5 + 10/10) = ceil(3.5) = 4
      agentId: 'testing-scout',
      name: 'Scout-Testing-v3.0',
      type: AgentType.TESTING,
      rarity: 2,
      level: 10,
      attack: 120,
      defense: 120,
      hp: 580,
      critRate: 9,
      computeCost: 4,
      elementAffinity: ElementType.GREEN,
      activeSkill: {
        skillId: 'bug-scan',
        name: 'Bug扫描',
        description: '暴露敌人弱点，增加受伤量',
        cooldown: 4,
        effects: [
          { type: SkillEffectType.DEBUFF_ENEMY, value: 0.2, duration: 2, target: 'enemy' },
        ],
      },
    },
    {
      // devops-docker: 2-star, level 8
      // ATK = round(65 * (1 + 0.08 * 7)) = round(65 * 1.56) = round(101.4) = 101
      // DEF = round(75 * (1 + 0.08 * 7)) = round(75 * 1.56) = round(117.0) = 117
      // HP  = round(420 * (1 + 0.05 * 7)) = round(420 * 1.35) = 567
      // CritRate = 5 + 2 * 2 = 9
      // ComputeCost = ceil(3 + (2-1)*0.5 + 8/10) = ceil(4.3) = 5
      agentId: 'devops-docker',
      name: 'Docker-DevOps-v2.5',
      type: AgentType.DEVOPS,
      rarity: 2,
      level: 8,
      attack: 101,
      defense: 117,
      hp: 567,
      critRate: 9,
      computeCost: 5,
      elementAffinity: ElementType.YELLOW,
      activeSkill: {
        skillId: 'emergency-scale',
        name: '紧急扩容',
        description: '稳定服务器，延长交付期限',
        cooldown: 6,
        effects: [
          { type: SkillEffectType.HEAL, value: 1, duration: 0, target: 'team' },
        ],
      },
    },
    {
      // hacker-zero: 2-star, level 10
      // ATK = round(75 * (1 + 0.08 * 9)) = round(75 * 1.72) = round(129.0) = 129
      // DEF = round(55 * (1 + 0.08 * 9)) = round(55 * 1.72) = round(94.6) = 95
      // HP  = round(380 * (1 + 0.05 * 9)) = round(380 * 1.45) = 551
      // CritRate = 5 + 2 * 2 = 9
      // ComputeCost = ceil(4 + (2-1)*0.5 + 10/10) = ceil(5.5) = 6
      agentId: 'hacker-zero',
      name: 'Zero-Hacker-v1.0',
      type: AgentType.HACKER,
      rarity: 2,
      level: 10,
      attack: 129,
      defense: 95,
      hp: 551,
      critRate: 9,
      computeCost: 6,
      elementAffinity: ElementType.PURPLE,
      activeSkill: {
        skillId: 'sql-inject',
        name: 'SQL注入',
        description: '破坏棋盘规则，转换tile为PURPLE',
        cooldown: 5,
        effects: [
          { type: SkillEffectType.BOARD_CHANGE, value: 5, duration: 0, target: 'board' },
        ],
      },
    },
  ],

  // Hardware snapshot:
  //   usedCompute = 5 + 4 + 4 + 5 + 6 = 24
  //   Setup: 2x CPU-basic(4 compute,2 power,2 heat) + 1x GPU-entry(5,3,4)
  //          + 1x GPU-high(12,7,10) + 1x cooler-air(0,0,5 cooling)
  //          + 1x cooler-water(0,1,12 cooling) + 1x PSU-high(0,15 limit,0)
  //   totalCompute = 4+5+12 = 21... need 1x TPU(8,2,3) to reach 29
  //   Revised: 2xCPU-basic + 1xGPU-entry + 1xTPU + 1xcooler-air + 1xcooler-water + 1xPSU-high
  //   totalCompute = 4+5+8 = 17... still short. Use: 3xCPU-basic + 1xGPU-high + 1xTPU
  //   totalCompute = 6+12+8 = 26
  //   powerUsage = 3+7+2+1 = 13 (cooler-water uses 1)
  //   totalPowerLimit = 15 (PSU-high)
  //   heatGen = 3+10+3 = 16
  //   totalCooling = 5+12 = 17
  //   heatGen(16) < totalCooling(17) => overloadLevel='none', overloadValue=0
  //   specialEffects: REDUCE_CD(1) from TPU, REDUCE_OVERLOAD_THRESHOLD(20) from cooler-water
  hardwareSnapshot: {
    totalCompute: 26,
    usedCompute: 24,
    totalPowerLimit: 15,
    currentPowerUsage: 13,
    totalCooling: 17,
    currentHeatGen: 16,
    overloadValue: 0,
    overloadLevel: 'none',
    specialEffects: [
      { type: 'REDUCE_CD', value: 1 },
      { type: 'REDUCE_OVERLOAD_THRESHOLD', value: 20 },
    ],
  },
  preparedAt: Date.now(),
};
